import { useMemo, useState } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Box, Grid, Typography } from '@mui/material';
import moment from 'moment';

import FHPagination from '../../base/FHPagination';
import ActionRequiredItem from './ActionRequiredItem';
import { FORTA_BLUE, FORTA_SUCCESS, FORTA_WHITE } from '../../../utils/colors';
import { ActionRequiredItemEnum } from '../../../constants';
import { FHSkeletonLoader } from '../../base';

import type {
  IActionRequiredItem,
  ITreatmentPlan,
  IBtSupervision,
  IGroupSupervision,
  IParentSupervision,
  IClientSupervision,
  IClientAssessment,
} from '../../../utils/types';

function ActionRequiredLoaderItem() {
  return (
    <Box
      sx={{
        p: 1.5,
        backgroundColor: FORTA_WHITE,
        borderRadius: '8px',
        minHeight: '210px',
        maxHeight: '210px',
        display: 'flex',
      }}
    >
      <FHSkeletonLoader width={'5px'} height={'190px'} />
      <Box sx={{ p: 1.5, ml: 0.5 }}>
        <FHSkeletonLoader sx={{ width: '210px' }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FHSkeletonLoader
            variant="circular"
            sx={{ width: '30px', height: '30px' }}
            containerStyles={{ mt: 2 }}
          />
          <FHSkeletonLoader sx={{ width: '150px' }} containerStyles={{ mt: 2 }} />
        </Box>
        <FHSkeletonLoader sx={{ width: '240px' }} containerStyles={{ mt: 2 }} />
        <FHSkeletonLoader sx={{ width: '220px' }} containerStyles={{ mt: 2 }} />
        <FHSkeletonLoader sx={{ width: '100px', borderRadius: 4 }} containerStyles={{ mt: 2 }} />
      </Box>
    </Box>
  );
}

interface ActionRequiredProps {
  loading: boolean;
  clientSupervisions: IClientSupervision[];
  groupSupervisions: IGroupSupervision[];
  parentSupervisions: IParentSupervision[];
  btSupervisions: IBtSupervision[];
  treatmentPlans: ITreatmentPlan[];
  clientAssessments: IClientAssessment[];
}

const SlideChunkSize = 8;

function ActionRequired(props: ActionRequiredProps) {
  const {
    btSupervisions,
    clientSupervisions,
    groupSupervisions,
    parentSupervisions,
    treatmentPlans,
    clientAssessments,
    loading,
  } = props;
  const [currentPage, setCurrentPage] = useState(1);

  const actionRequired = useMemo(() => {
    const mapToActionRequiredItem = (
      items: any[],
      type: ActionRequiredItemEnum,
    ): IActionRequiredItem[] =>
      items.map((item) => ({
        id: item?.id ?? 0,
        type: type,
        item,
        date_start:
          type === ActionRequiredItemEnum.TreatmentPlan ? item.status?.date : item.date_start,
      }));

    return [
      ...mapToActionRequiredItem(btSupervisions, ActionRequiredItemEnum.Bt),
      ...mapToActionRequiredItem(clientSupervisions, ActionRequiredItemEnum.Client),
      ...mapToActionRequiredItem(groupSupervisions, ActionRequiredItemEnum.GroupParent),
      ...mapToActionRequiredItem(parentSupervisions, ActionRequiredItemEnum.Parent),
      ...mapToActionRequiredItem(treatmentPlans, ActionRequiredItemEnum.TreatmentPlan),
      ...mapToActionRequiredItem(clientAssessments, ActionRequiredItemEnum.Assessment),
    ].sort((a, b) => moment(a.date_start).diff(moment(b.date_start))); // Sort by age of the item, oldest first
  }, [
    btSupervisions,
    clientSupervisions,
    groupSupervisions,
    parentSupervisions,
    treatmentPlans,
    clientAssessments,
  ]);

  const chunkedArray = useMemo(() => {
    const group = [];
    for (let i = 0; i < actionRequired.length; i += SlideChunkSize) {
      group.push(actionRequired.slice(i, i + SlideChunkSize));
    }
    return group;
  }, [actionRequired]);

  const currentItems = useMemo(() => {
    return chunkedArray[currentPage - 1] || [];
  }, [chunkedArray, currentPage]);

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  return loading ? (
    <Box>
      <FHSkeletonLoader containerStyles={{ my: 1, mb: 2 }} width={'350px'} />
      <Grid container spacing={{ xs: 2, sm: 2, md: 2 }}>
        {Array.from({ length: 8 }).map((_, i) => (
          <Grid key={i} item xs={12} sm={6} md={4} lg={3}>
            <ActionRequiredLoaderItem />
          </Grid>
        ))}
      </Grid>
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <FHSkeletonLoader containerStyles={{ mt: 2.5, mb: 1.5 }} width={'150px'} />
      </Box>
    </Box>
  ) : (
    <Box sx={{ flexGrow: 1 }}>
      {actionRequired.length > 0 ? (
        <>
          <Typography fontSize={15} textAlign="left" mb={2} color={FORTA_BLUE.primary}>
            You have {actionRequired.length} documents that need to be signed
          </Typography>
          <Grid container spacing={{ xs: 2, sm: 2, md: 2 }}>
            {currentItems.map((item: IActionRequiredItem) => (
              <Grid key={`${item.id}-${item.type}`} item xs={12} sm={6} md={4} lg={3}>
                <ActionRequiredItem actionRequiredItem={item} />
              </Grid>
            ))}
          </Grid>
          {chunkedArray.length > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 2 }}>
              <FHPagination
                count={chunkedArray.length}
                currentPage={currentPage}
                handlePageChange={handlePageChange}
              />
            </Box>
          )}
        </>
      ) : (
        <Box
          sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <CheckCircleIcon sx={{ color: FORTA_SUCCESS }} />
          <Typography ml={0.5}>You&apos;re all caught up! Thank you!</Typography>
        </Box>
      )}
    </Box>
  );
}

export default ActionRequired;
